import React, { useState, useEffect } from 'react';

interface Contact {
  [key: string]: string;
}

interface EmailLink {
  to: string;
  subject: string;
  body: string;
  storeName: string;
}

export const MassEmail: React.FC = () => {
  const [contactInfo, setContactInfo] = useState('');
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [emailLinks, setEmailLinks] = useState<EmailLink[]>([]);

  // Process the contacts and templates to generate mailto links
  useEffect(() => {
    if (!contactInfo.trim() || !subjectTemplate.trim() || !bodyTemplate.trim()) {
      setEmailLinks([]);
      return;
    }

    try {
      // Parse contact info - 2-line format:
      // Line 1: customer name
      // Line 2: customer email
      const contacts: Contact[] = [];
      const lines = contactInfo.split('\n').filter(line => line.trim());

      // Process lines in pairs (name, email)
      for (let i = 0; i < lines.length; i += 2) {
        const name = lines[i]?.trim();
        const email = lines[i + 1]?.trim();

        // Validate we have both name and email
        if (name && email) {
          contacts.push({
            name: name,
            'store name': name, // Keep 'store name' for backward compatibility with templates
            'customer name': name, // Support {{customer name}} variable
            email: email,
            'customer email': email // Support {{customer email}} variable
          });
        } else if (name && !email) {
          // Incomplete pair - name without email
          console.warn(`Incomplete contact at line ${i + 1}: missing email for "${name}"`);
        }
      }

      // Generate mailto links for each contact
      const links: EmailLink[] = contacts.map(contact => {
        // Replace placeholders in subject
        let personalizedSubject = subjectTemplate;
        Object.keys(contact).forEach(key => {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          personalizedSubject = personalizedSubject.replace(regex, contact[key]);
        });

        // Replace placeholders in body
        let personalizedBody = bodyTemplate;
        Object.keys(contact).forEach(key => {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          personalizedBody = personalizedBody.replace(regex, contact[key]);
        });

        return {
          to: contact['email'] || '',
          subject: personalizedSubject,
          body: personalizedBody,
          storeName: contact['store name'] || contact['name'] || ''
        };
      });

      setEmailLinks(links);
    } catch (error) {
      setEmailLinks([]);
    }
  }, [contactInfo, subjectTemplate, bodyTemplate]);

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontFamily: 'monospace',
    border: '1px solid #ddd',
    borderRadius: '4px',
    resize: 'vertical',
    minHeight: '150px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '30px'
  };

  const helpTextStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#666',
    marginTop: '6px',
    fontStyle: 'italic'
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#333' }}>Mass Email</h1>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>
          Create personalized emails using templates and contact information
        </p>
      </header>

      <div style={sectionStyle}>
        <label style={labelStyle}>Contact Info</label>
        <textarea
          style={fieldStyle}
          value={contactInfo}
          onChange={e => setContactInfo(e.target.value)}
          placeholder="Enter contacts (2 lines per customer):&#10;&#10;Store A&#10;store-a@example.com&#10;Store B&#10;store-b@example.com&#10;Store C&#10;store-c@example.com"
        />
        <p style={helpTextStyle}>
          Format: Enter customer name on first line, email address on second line. Repeat for each customer.
        </p>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Subject Template</label>
        <textarea
          style={{ ...fieldStyle, minHeight: '80px' }}
          value={subjectTemplate}
          onChange={e => setSubjectTemplate(e.target.value)}
          placeholder="Enter your subject template&#10;Example: Update from {{store name}}"
        />
        <p style={helpTextStyle}>
          Use {'{{variableName}}'} for placeholders (e.g., {'{{customer name}}'}, {'{{customer email}}'},{' '}
          {'{{store name}}'}, {'{{email}}'})
        </p>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Body Template</label>
        <textarea
          style={fieldStyle}
          value={bodyTemplate}
          onChange={e => setBodyTemplate(e.target.value)}
          placeholder="Enter your email body template&#10;Example:&#10;Hello {{customer name}},&#10;&#10;This is a message for {{customer email}}.&#10;&#10;Best regards"
        />
        <p style={helpTextStyle}>
          Use {'{{customer name}}'} and {'{{customer email}}'} for personalization. Other variables: {'{{store name}}'},{' '}
          {'{{email}}'}
        </p>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Generated Mailto Links ({emailLinks.length})</label>
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '16px',
            backgroundColor: '#f9f9f9',
            minHeight: '200px'
          }}
        >
          {emailLinks.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic' }}>
              Enter contact info, subject template, and body template to generate mailto links...
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {emailLinks.map((link, index) => {
                const mailtoUrl = `mailto:${encodeURIComponent(link.to)}?subject=${encodeURIComponent(link.subject)}&body=${encodeURIComponent(link.body)}`;

                return (
                  <li
                    key={index}
                    style={{
                      marginBottom: '16px',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#333' }}>
                        {index + 1}. {link.storeName}
                      </strong>
                    </div>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: '#666' }}>To: {link.to}</div>
                    <div style={{ marginBottom: '4px', fontSize: '14px', color: '#666' }}>Subject: {link.subject}</div>
                    <div style={{ marginTop: '8px' }}>
                      <a
                        href={mailtoUrl}
                        style={{
                          display: 'inline-block',
                          padding: '8px 16px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        Open in Email Client
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <p style={helpTextStyle}>Click the buttons to open your email client with pre-filled subject and body</p>
      </div>
    </div>
  );
};
